import { Box, Drawer, DrawerContent, DrawerOverlay, useMediaQuery, VStack } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import PropTypes from 'prop-types'
import { useMFNav } from './MFNav.Provider'

// Default styles for the MF sidebar container.
const mFSidebarContainerStyles = {
  bg: 'surface',
  color: 'fg',
  _after: {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    w: '100%',
    h: '100%',
    bg: 'inherit',
    borderRight: '1px solid',
    borderColor: 'neutral.50',
    clipPath: 'polygon(100% 0%, 100% 0px, 98% 0px, 98% 44px, 100% 44px, 100% 100%, 0% 100%, 0% 0%)',
    zIndex: -1
  }
}

/**
 * MFSidebarContainer component handles the layout and display of the sidebar.
 *
 * This component adapts its display based on the screen size and the current state of the navigation menu.
 * It either displays a sidebar as a fixed box on larger screens or as a drawer on mobile devices.
 * The sidebar can be in a "dock" state, which expands on hover, or a "drawer" state for mobile views.
 *
 * @param {Object} props - The properties for the component.
 * @param {React.ReactNode} props.children - The children components to be rendered inside the sidebar.
 * @returns {JSX.Element} The rendered sidebar container.
 */
export default function MFNavSidebarContainer({ children }) {
  const {
    menu: { size, state },
    isOpen,
    onClose
  } = useMFNav()

  const [isMobile] = useMediaQuery('(max-width: 29.98em)')

  if (size.value === 'drawer') {
    return (
      <Drawer
        placement="left"
        // variant="nav"
        // size="nav"
        isOpen={isOpen}
        onClose={onClose}
        zIndex="tooltip"
        sx={mFSidebarContainerStyles}
      >
        <DrawerOverlay onClick={onClose} zIndex="overlay" />
        <DrawerContent maxW={isMobile ? 'full' : '56'}>
          <VStack h="full" justifyContent="space-between" alignItems="flex-start">
            {children}
          </VStack>
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Box
      as={motion.div}
      layout
      layoutRoot
      initial={{ width: '4rem' }}
      whileHover={{ width: '14rem' }}
      sx={mFSidebarContainerStyles}
      hidden={size.value === 'none'}
      position="fixed"
      top="0"
      left="0"
      h="100vh"
      minW={size.value === 'dock' && !state.isHovered ? 16 : 56}
      zIndex="modal"
      onMouseEnter={() => state.setIsHovered(true)}
      onMouseLeave={() => state.setIsHovered(false)}
    >
      <VStack h="full" justifyContent="space-between" alignItems="flex-start">
        {children}
      </VStack>
    </Box>
  )
}

MFNavSidebarContainer.propTypes = {
  children: PropTypes.node.isRequired
}
